<script setup lang="ts">
import { useManager } from '@/composables/useManager';
import { usePlatform } from '@/composables/usePlatform';
import { useUserStore } from '@/stores/useUserStore';
import type { Employee } from '@/types/api';
import { getCurrentRestaurantTime } from '@/utils/time';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import Button from 'primevue/button';
import Calendar from 'primevue/calendar';
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import { useToast } from 'primevue/usetoast';
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

dayjs.extend(utc);
dayjs.extend(timezone);

interface Props {
  visible: boolean;
  worker: Employee | null;
}

interface Emits {
  (e: 'update:visible', value: boolean): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { t, locale } = useI18n();
const toast = useToast();
const { exportWorkerHours } = useManager();
const userStore = useUserStore();
const user = ref(userStore.user);
const isMobilePhone = usePlatform().isMobilePhone;

// Dialog state
const showDialog = ref(props.visible);
watch(
  () => props.visible,
  (v) => (showDialog.value = v),
);
watch(showDialog, (v) => emit('update:visible', v));

// Export state
const loading = ref(false);

// Get restaurant timezone
const restaurantTimezone = computed(() => {
  return user.value?.active_restaurant?.timezone || 'UTC';
});

// Date range presets
const dateRangePresets = computed(() => [
  { label: t('employee.timeclock.export.presets.currentMonth'), value: 'current_month' },
  { label: t('employee.timeclock.export.presets.lastMonth'), value: 'last_month' },
  { label: t('employee.timeclock.export.presets.last3Months'), value: 'last_3_months' },
  { label: t('employee.timeclock.export.presets.thisYear'), value: 'this_year' },
  { label: t('employee.timeclock.export.presets.customRange'), value: 'custom' },
]);

// Form state
const form = reactive({
  preset: 'current_month',
  customStartDate: null as Date | null,
  customEndDate: null as Date | null,
});

// Computed date range based on preset using restaurant timezone
const effectiveDateRange = computed(() => {
  // Get current time in restaurant timezone
  const now = getCurrentRestaurantTime(restaurantTimezone.value);

  switch (form.preset) {
    case 'current_month': {
      const startOfMonth = now.startOf('month');
      const endOfMonth = now.endOf('month');
      return {
        start: startOfMonth.toDate(),
        end: endOfMonth.toDate(),
      };
    }
    case 'last_month': {
      const lastMonth = now.subtract(1, 'month');
      const lastMonthStart = lastMonth.startOf('month');
      const lastMonthEnd = lastMonth.endOf('month');
      return {
        start: lastMonthStart.toDate(),
        end: lastMonthEnd.toDate(),
      };
    }
    case 'last_3_months': {
      const threeMonthsAgo = now.subtract(3, 'month').startOf('month');
      const endOfCurrentMonth = now.endOf('month');
      return {
        start: threeMonthsAgo.toDate(),
        end: endOfCurrentMonth.toDate(),
      };
    }
    case 'this_year': {
      const startOfYear = now.startOf('year');
      const endOfYear = now.endOf('year');
      return {
        start: startOfYear.toDate(),
        end: endOfYear.toDate(),
      };
    }
    case 'custom':
      return {
        start: form.customStartDate,
        end: form.customEndDate,
      };
    default: {
      const startOfMonth = now.startOf('month');
      const endOfMonth = now.endOf('month');
      return {
        start: startOfMonth.toDate(),
        end: endOfMonth.toDate(),
      };
    }
  }
});

// Validation
const isValidDateRange = computed(() => {
  const range = effectiveDateRange.value;
  if (!range.start || !range.end) return false;
  return range.start <= range.end;
});

const canExport = computed(() => {
  return props.worker && isValidDateRange.value && !loading.value;
});

// Format date for display using restaurant timezone
const formatDateRange = computed(() => {
  const range = effectiveDateRange.value;
  if (!range.start || !range.end) return '';

  // Convert dates to restaurant timezone for display
  const startInRestaurantTz = dayjs(range.start).tz(restaurantTimezone.value);
  const endInRestaurantTz = dayjs(range.end).tz(restaurantTimezone.value);

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: restaurantTimezone.value,
  };

  return `${startInRestaurantTz.toDate().toLocaleDateString(locale.value, options)} - ${endInRestaurantTz.toDate().toLocaleDateString(locale.value, options)}`;
});

// Reset form when dialog opens
watch(showDialog, (visible) => {
  if (visible) {
    form.preset = 'current_month';
    form.customStartDate = null;
    form.customEndDate = null;
  }
});

// Handle export
const handleExport = async () => {
  if (!props.worker || !canExport.value) return;

  loading.value = true;
  try {
    const range = effectiveDateRange.value;

    // Convert dates to restaurant timezone and format as YYYY-MM-DD
    const startDate = dayjs(range.start).tz(restaurantTimezone.value).format('YYYY-MM-DD');
    const endDate = dayjs(range.end).tz(restaurantTimezone.value).format('YYYY-MM-DD');

    await exportWorkerHours(props.worker.restaurant_user_id, startDate, endDate);

    toast.add({
      severity: 'success',
      summary: t('common.success'),
      detail: t('employee.timeclock.export.success', { range: formatDateRange.value }),
      life: 5000,
    });

    showDialog.value = false;
  } catch (error) {
    console.error('Export error:', error);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('employee.timeclock.export.error'),
      life: 5000,
    });
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <Dialog v-model:visible="showDialog" modal :header="t('employee.timeclock.export.title')" :style="{ width: '32rem', maxWidth: '90vw' }">
    <div class="flex flex-col gap-4">
      <!-- Worker info -->
      <div v-if="worker" class="text-center">
        <h4 class="m-0">{{ worker.first_name }} {{ worker.last_name }}</h4>
        <p class="m-0 text-sm text-gray-500">{{ t('employee.timeclock.export.workingPeriodsExport') }}</p>
      </div>

      <!-- Date range preset -->
      <div class="flex flex-col gap-2">
        <label for="preset" class="font-semibold">{{ t('employee.timeclock.export.dateRange') }}</label>
        <Dropdown
          id="preset"
          v-model="form.preset"
          :options="dateRangePresets"
          option-label="label"
          option-value="value"
          :placeholder="t('employee.timeclock.export.selectDateRange')"
          class="w-full"
        />
      </div>

      <!-- Custom date range -->
      <div v-if="form.preset === 'custom'" class="flex flex-col gap-3">
        <div class="flex flex-col gap-2">
          <label for="start-date" class="font-semibold">{{ t('employee.timeclock.export.startDate') }}</label>
          <Calendar
            id="start-date"
            v-model="form.customStartDate"
            date-format="dd.mm.yy"
            :placeholder="t('employee.timeclock.export.selectStartDate')"
            show-icon
            class="w-full"
            :touch-u-i="isMobilePhone"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label for="end-date" class="font-semibold">{{ t('employee.timeclock.export.endDate') }}</label>
          <Calendar
            id="end-date"
            v-model="form.customEndDate"
            date-format="dd.mm.yy"
            :placeholder="t('employee.timeclock.export.selectEndDate')"
            show-icon
            class="w-full"
            :touch-u-i="isMobilePhone"
          />
        </div>
      </div>

      <!-- Date range preview -->
      <div v-if="isValidDateRange" class="rounded border border-gray-200 bg-gray-50 p-3">
        <h5 class="m-0 mb-2">{{ t('employee.timeclock.export.exportPreview') }}</h5>
        <p class="m-0 text-sm">
          <strong>{{ t('employee.timeclock.export.dateRange') }}:</strong> {{ formatDateRange }}
        </p>
        <p class="m-0 mt-1 text-xs text-gray-600">{{ t('common.timezone') }}: {{ restaurantTimezone }}</p>
      </div>

      <!-- Error message -->
      <div v-if="!isValidDateRange && (form.customStartDate || form.customEndDate)" class="text-sm text-red-500">
        {{ t('employee.timeclock.export.invalidDateRange') }}
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button :label="t('common.cancel')" severity="secondary" @click="showDialog = false" />
        <Button
          :label="t('employee.timeclock.export.exportCSV')"
          icon="pi pi-download"
          :loading="loading"
          :disabled="!canExport"
          @click="handleExport"
        />
      </div>
    </template>
  </Dialog>
</template>
